// Local-only persistence for the Case Timeline Builder — a hand-rolled,
// promisified IndexedDB wrapper (no external dependency; the API surface
// needed here is small: put/get/getAll/delete across two stores). Nothing
// here ever leaves the browser except via the explicit exportTimelineJson()/
// importTimelineFromJson() functions the user triggers themselves.
//
// Two object stores:
//   timelines — one record per saved case (metadata only, no images/attachments,
//               so the landing page can list cheaply).
//   events    — one record per event, indexed by timelineId.

import { saveAs } from 'file-saver';

const DB_NAME = 'caseTimelineBuilder';
const DB_VERSION = 1;
const SCHEMA_VERSION = 1;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('timelines')) {
        db.createObjectStore('timelines', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('events')) {
        const store = db.createObjectStore('events', { keyPath: 'id' });
        store.createIndex('by_timeline', 'timelineId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(storeNames, mode) {
  const db = await openDb();
  return db.transaction(storeNames, mode);
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function makeId() {
  return (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const safeFileName = s =>
  (s || 'case-timeline').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'case-timeline';

function computeSortTimestamp(dateType, date) {
  if (!date) return 0;
  const iso = dateType === 'exact' ? date : `${date}T00:00:00`;
  const d = new Date(iso);
  return isNaN(d) ? 0 : d.getTime();
}

async function recomputeTimelineSummary(timelineId, t) {
  const idx = t.objectStore('events').index('by_timeline');
  const events = await reqToPromise(idx.getAll(timelineId));
  const timelinesStore = t.objectStore('timelines');
  const timeline = await reqToPromise(timelinesStore.get(timelineId));
  if (!timeline) return;
  const timestamps = events.map(e => e.sortTimestamp).filter(Boolean);
  const dateRange = timestamps.length
    ? { start: Math.min(...timestamps), end: Math.max(...timestamps) }
    : { start: null, end: null };
  await reqToPromise(timelinesStore.put({ ...timeline, eventCount: events.length, dateRange, updatedAt: Date.now() }));
}

// ── Timelines ──

export async function listTimelines() {
  const t = await tx('timelines', 'readonly');
  const all = await reqToPromise(t.objectStore('timelines').getAll());
  return all.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function getTimeline(id) {
  const t = await tx('timelines', 'readonly');
  return reqToPromise(t.objectStore('timelines').get(id));
}

export async function createTimeline({ title, description = '', caseNumber = '', investigator = '' } = {}) {
  const now = Date.now();
  const timeline = {
    id: makeId(),
    schemaVersion: SCHEMA_VERSION,
    title: (title || '').trim() || 'Untitled case',
    description: description.trim(),
    caseNumber: caseNumber.trim(),
    investigator: investigator.trim(),
    createdAt: now,
    updatedAt: now,
    eventCount: 0,
    dateRange: { start: null, end: null },
  };
  const t = await tx('timelines', 'readwrite');
  await reqToPromise(t.objectStore('timelines').put(timeline));
  if (navigator.storage && navigator.storage.persist) navigator.storage.persist().catch(() => {});
  return timeline;
}

export async function updateTimelineMeta(id, patch) {
  const t = await tx('timelines', 'readwrite');
  const store = t.objectStore('timelines');
  const existing = await reqToPromise(store.get(id));
  if (!existing) return null;
  const updated = { ...existing, ...patch, updatedAt: Date.now() };
  await reqToPromise(store.put(updated));
  return updated;
}

export async function deleteTimeline(id) {
  const events = await listEvents(id);
  const t = await tx(['timelines', 'events'], 'readwrite');
  await Promise.all(events.map(e => reqToPromise(t.objectStore('events').delete(e.id))));
  await reqToPromise(t.objectStore('timelines').delete(id));
}

// ── Events ──

export async function listEvents(timelineId) {
  const t = await tx('events', 'readonly');
  const idx = t.objectStore('events').index('by_timeline');
  const all = await reqToPromise(idx.getAll(timelineId));
  return all.sort((a, b) => a.sortTimestamp - b.sortTimestamp);
}

export async function saveEvent(event) {
  const now = Date.now();
  const record = {
    ...event,
    id: event.id || makeId(),
    sortTimestamp: computeSortTimestamp(event.dateType, event.date),
    createdAt: event.createdAt || now,
    updatedAt: now,
  };
  const t = await tx(['events', 'timelines'], 'readwrite');
  await reqToPromise(t.objectStore('events').put(record));
  await recomputeTimelineSummary(record.timelineId, t);
  return record;
}

export async function deleteEvent(id, timelineId) {
  const t = await tx(['events', 'timelines'], 'readwrite');
  await reqToPromise(t.objectStore('events').delete(id));
  await recomputeTimelineSummary(timelineId, t);
}

// ── JSON export/import (portability + backup — IndexedDB is best-effort
// browser storage, not a real save file, so this is the durable copy) ──

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl) {
  return fetch(dataUrl).then(r => r.blob());
}

export async function exportTimelineJson(timelineId) {
  const timeline = await getTimeline(timelineId);
  if (!timeline) throw new Error('Timeline not found.');
  const events = await listEvents(timelineId);
  const portableEvents = await Promise.all(events.map(async e => {
    if (!e.attachment) return e;
    const dataUrl = await blobToDataUrl(e.attachment.blob);
    const { blob, ...rest } = e.attachment; // eslint-disable-line no-unused-vars
    return { ...e, attachment: { ...rest, dataUrl } };
  }));
  const payload = { schemaVersion: SCHEMA_VERSION, exportedAt: Date.now(), timeline, events: portableEvents };
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
  saveAs(blob, `${safeFileName(timeline.title)}.json`);
}

export async function importTimelineFromJson(file) {
  const text = await file.text();
  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error("That file isn't valid JSON.");
  }
  if (!payload || typeof payload !== 'object' || !payload.timeline || !Array.isArray(payload.events)) {
    throw new Error("That file doesn't look like a Case Timeline Builder export.");
  }

  const now = Date.now();
  const newTimelineId = makeId();
  const newTimeline = { ...payload.timeline, id: newTimelineId, schemaVersion: SCHEMA_VERSION, createdAt: now, updatedAt: now };

  const newEvents = await Promise.all(payload.events.map(async e => {
    let attachment = null;
    if (e.attachment && e.attachment.dataUrl) {
      const blob = await dataUrlToBlob(e.attachment.dataUrl);
      const { dataUrl, ...rest } = e.attachment; // eslint-disable-line no-unused-vars
      attachment = { ...rest, blob };
    }
    return { ...e, id: makeId(), timelineId: newTimelineId, attachment };
  }));

  const t = await tx(['timelines', 'events'], 'readwrite');
  await reqToPromise(t.objectStore('timelines').put(newTimeline));
  await Promise.all(newEvents.map(e => reqToPromise(t.objectStore('events').put(e))));
  await recomputeTimelineSummary(newTimelineId, t);
  return newTimelineId;
}
